import { useCallback, useMemo } from "react";
import parse, { HTMLReactParserOptions } from "html-react-parser";
import _ from "lodash";

import { colors } from "theme/palette";

import Box from "components/Box";
import Stack from "components/Stack";
import Text from "components/Text";

import PrintableMemoFieldTitle from "pages/Deal/PrintableMemo/components/PrintableMemoFieldTitle";

import { IPackageDetailsField } from "entities/Checklist/sdk";
import FieldValue from "entities/Field/components/FormTextField/FieldValue";
import { FIELD_PLACEHOLDER_REGEX } from "entities/Field/components/FormTextField/FormText/constants";
import { getFieldIdFromPlaceholder } from "entities/Field/components/FormTextField/FormText/utils";

interface IPrintableMemoFormTextField {
  field: IPackageDetailsField;
  isNASection: boolean;
}

const PrintableMemoFormTextField = ({
  field,
  isNASection,
}: IPrintableMemoFormTextField) => {
  const isNAField = useMemo(
    () => field.n_a_status || isNASection,
    [field, isNASection]
  );

  const parseAndReplaceWithComponents = useCallback(
    ({ text }: { text: string }) => {
      let lastIndex = 0;
      const components: React.ReactNode[] = [];

      let match = FIELD_PLACEHOLDER_REGEX.exec(text);

      while (match !== null) {
        const placeholder = match[0];
        const beforeText = text.slice(lastIndex, match.index);
        components.push(beforeText);

        const fieldId = getFieldIdFromPlaceholder({ placeholder });
        const fieldTemplateMeta = _.find(
          field.template_meta,
          (field) => field.field_id === fieldId
        );

        if (_.isUndefined(fieldTemplateMeta)) {
          components.push(
            <Text fontWeight="bold" display="inline-block">
              Missing field
            </Text>
          );
        } else {
          components.push(<FieldValue templateMeta={fieldTemplateMeta} />);
        }

        lastIndex = match.index + match[0].length;
        match = FIELD_PLACEHOLDER_REGEX.exec(text);
      }

      const remainingText = text.slice(lastIndex);
      components.push(remainingText);

      return components;
    },
    [field.template_meta]
  );

  const advancedParse = useCallback(
    ({ htmlString }: { htmlString: string }) => {
      const options = {
        replace: ({ data }: any) => {
          if (!_.isNil(data) && data !== "") {
            return <>{parseAndReplaceWithComponents({ text: data })}</>;
          }
        },
      } as HTMLReactParserOptions;

      return parse(htmlString, options);
    },
    [parseAndReplaceWithComponents]
  );

  const toRender = useMemo(
    () =>
      advancedParse({
        htmlString: !_.isNil(field.template) ? field.template : "",
      }),
    [field.template, advancedParse]
  );

  return (
    <Box sx={{ borderBottom: `1px solid ${colors.blue20}` }}>
      <Stack direction="row" alignItems="center" data-testid="dd-field">
        <PrintableMemoFieldTitle field={field} disabled={isNAField} />

        {isNAField && (
          <Text sx={{ opacity: 0.5 }} data-testid="field-value-na">
            N/A
          </Text>
        )}
      </Stack>
      {!isNAField && field.template_meta && field.template && (
        <Box sx={{ marginY: 1 }}>
          <Text>{toRender}</Text>
        </Box>
      )}
    </Box>
  );
};

export default PrintableMemoFormTextField;
